import Vuex from "vuex";
import Vue from "vue";
import menuUtil from "../utils/index";
import router, { resetRouter } from "../router/router";
import hump from "../utils/humpToLine";
import Message from "@/utils/message";
import i18n from "../utils/i18n";

Vue.use(Vuex);

// 从 localStorage 获取数据的辅助函数
const getStorageItem = (key, defaultValue = "") => {
  try {
    return localStorage.getItem(key) || defaultValue;
  } catch (e) {
    return defaultValue;
  }
};

// 设置 localStorage 的辅助函数
const setStorageItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // ignore
  }
};

// 移除 localStorage 的辅助函数
const removeStorageItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // ignore
  }
};

export default new Vuex.Store({
  state: {
    menuList: [],
    dynamicRoutesReady: false,
    initFlag: true,
    collapse: false,
    tagsTop: true,
    innerHeight: window.innerHeight,
    tagList: [],
    visitedViews: [],
    permissions: [],
    roles: [],
    // 用户信息 - 从 localStorage 恢复或登录时设置
    token: getStorageItem("token"),
    uid: getStorageItem("uid"),
    userName: getStorageItem("userName"),
    nickName: getStorageItem("nickName"),
    avatar: getStorageItem("avatar"),
    zone: getStorageItem("zone"),
    parentId: getStorageItem("parentId"),
    checked: getStorageItem("checked"),
    lang: getStorageItem("lang", "en_US"),
    isDot: false,
    privacyVisible: false
  },
  mutations: {
    set(state, payload) {
      for (let i in payload) {
        state[i] = payload[i];
      }
    },
    LOGIN(state, user) {
      // 持久化到 localStorage
      setStorageItem("token", user.token);
      setStorageItem("nickName", user.nickName);
      setStorageItem("uid", user.uid);
      setStorageItem("checked", user.checked);
      setStorageItem("userName", user.userName);
      setStorageItem("zone", user.timeZone);
      // 同步更新 state
      state.token = user.token;
      state.nickName = user.nickName;
      state.uid = user.uid;
      state.checked = user.checked;
      state.userName = user.userName;
      state.zone = user.timeZone;
      state.permissions = user.authority;
      if (user.menu && user.menu.length > 0) {
        let fmRoutes = menuUtil.formatRoutes(user.menu);
        router.addRoutes(fmRoutes);
        state.menuList = user.menu;
        state.dynamicRoutesReady = true;
      } else {
        Message.warning({
          message: i18n.t("api.np"),
          duration: 2000,
          showClose: false
        });
        router.push("/login");
      }
    },
    LOGIN_OUT(state) {
      // 清除 localStorage
      removeStorageItem("token");
      removeStorageItem("avatar");
      removeStorageItem("nickName");
      removeStorageItem("uid");
      removeStorageItem("checked");
      removeStorageItem("userType");
      removeStorageItem("userName");
      removeStorageItem("zone");
      removeStorageItem("parentId");
      // 清除 state
      state.token = "";
      state.uid = "";
      state.userName = "";
      state.nickName = "";
      state.avatar = "";
      state.zone = "";
      state.parentId = "";
      state.checked = "";
      state.permissions = [];
      state.tagList = [];
      state.menuList = [];
      state.visitedViews = [];
      state.dynamicRoutesReady = false;
      resetRouter();
    },
    INIT_FLAG(state, initFlag) {
      state.initFlag = initFlag;
    },
    INIT_MENU_LIST(state, menuAuth) {
      if (menuAuth.menu && menuAuth.menu.length > 0) {
        state.menuList = menuAuth.menu;
        state.dynamicRoutesReady = true;
      }
      if (menuAuth.authority && menuAuth.authority.length > 0) {
        state.permissions = menuAuth.authority;
      }
    },
    COLLAPSE(state, collapse) {
      state.collapse = collapse;
    },
    TAGS_TOP(state, tagsTop) {
      state.tagsTop = tagsTop;
    },
    INNER_HEIGHT(state, height) {
      state.innerHeight = height;
    },
    ADD_VISITED_VIEWS: (state, view) => {
      if (state.visitedViews.some(v => v.path === view.fullPath)) {
        return;
      }
      state.visitedViews.push({
        name: view.meta.title || view.name,
        path: view.path,
        title: view.meta.title || "not name"
      });
      if (view.meta.keepAlive === "0") {
        let path = view.path.split("/");
        state.tagList.push(hump.default.toHump(path[path.length - 1]));
      }
    },
    DEL_VISITED_VIEWS: (state, view) => {
      for (const [i, v] of state.visitedViews.entries()) {
        if (v.path === view.path) {
          state.visitedViews.splice(i, 1);
          state.tagList.splice(i, 1);
          break;
        }
      }
    },
    DEL_OTHERS_VIEWS: (state, view) => {
      state.visitedViews = state.visitedViews.filter(item => {
        return item.path === view.fullPath;
      });
      state.tagList = state.tagList.filter(item => {
        let path = view.path.split("/");
        return item === hump.default.toHump(path[path.length - 1]);
      });
    },
    DEL_ALL_VIEWS: state => {
      state.visitedViews = [];
      state.tagList = [];
    },
    UPDATE_USER: (state, user) => {
      state.nickName = user.nickName;
    },
    UPDATE_AVATAR: (state, avatar) => {
      state.avatar = avatar;
      setStorageItem("avatar", avatar);
    },
    SET_LANG: (state, lang) => {
      state.lang = lang;
      setStorageItem("lang", lang);
    },
    SET_PARENT_ID: (state, parentId) => {
      state.parentId = parentId;
      setStorageItem("parentId", parentId);
    }
  },
  // Getters - 提供统一的访问入口
  getters: {
    token: state => state.token,
    uid: state => state.uid,
    userName: state => state.userName,
    nickName: state => state.nickName,
    avatar: state => state.avatar,
    zone: state => state.zone,
    parentId: state => state.parentId,
    lang: state => state.lang,
    isLoggedIn: state => !!state.token
  },
  /*异步的去commit操作*/
  actions: {
    login({ commit }, user) {
      commit("LOGIN", user);
      return Promise.resolve(user);
    },
    loginOut({ commit }) {
      commit("LOGIN_OUT");
    },
    initMenuList({ commit }, menuList) {
      commit("INIT_MENU_LIST", menuList);
    },
    initFlag({ commit }, flag) {
      commit("INIT_FLAG", flag);
    },
    collapse({ commit }, collapse) {
      commit("COLLAPSE", collapse);
    },
    tagsTop({ commit }, tagsTop) {
      commit("TAGS_TOP", tagsTop);
    },
    innerHeight({ commit }, height) {
      commit("INNER_HEIGHT", height);
    },
    addVisitedViews({ commit }, view) {
      commit("ADD_VISITED_VIEWS", view);
    },
    delVisitedViews({ commit, state }, view) {
      return new Promise(resolve => {
        commit("DEL_VISITED_VIEWS", view);
        resolve([...state.visitedViews]);
      });
    },
    delOthersViews({ commit, state }, view) {
      return new Promise(resolve => {
        commit("DEL_OTHERS_VIEWS", view);
        resolve([...state.visitedViews]);
      });
    },
    delAllViews({ commit, state }) {
      return new Promise(resolve => {
        commit("DEL_ALL_VIEWS");
        resolve([...state.visitedViews]);
      });
    },
    updateUser({ commit }, user) {
      commit("UPDATE_USER", user);
    },
    updateAvatar({ commit }, avatar) {
      commit("UPDATE_AVATAR", avatar);
    },
    setLang({ commit }, lang) {
      commit("SET_LANG", lang);
      i18n.locale = lang;
      return Promise.resolve(lang);
    },
    setParentId({ commit }, parentId) {
      commit("SET_PARENT_ID", parentId);
      return Promise.resolve(parentId);
    }
  }
});
